import { React, useState, useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getTimeSlotsService, schedulePropertyVisit } from "../../services/SchedulePropertyVisit";
import { useSearchParams } from "react-router-dom";
import CustomSelect from "../../components/CustomSelect";
import { useTranslation } from "react-i18next";

const labelStyle = {
  display: "block",
  color: "#1A8CFF",
  fontWeight: 500,
  fontSize: "12px",
  fontFamily: "Poppins, sans-serif",
  marginBottom: "6px",
};

const dateInputStyle = {
  width: "100%",
  height: "44px",
  padding: "0 12px",
  border: "1.5px solid #E8ECF0",
  borderRadius: "10px",
  backgroundColor: "#F5F7FA",
  fontFamily: "Poppins, sans-serif",
  fontSize: "13px",
  color: "#1B2559",
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
};

const sectionCard = {
  backgroundColor: "#F9FAFB",
  border: "1px solid #E8ECF0",
  borderRadius: "12px",
  padding: "16px 20px",
  marginBottom: "16px",
};

const ScheduleVisit = ({ id, setScheduleAppointment, getPropertyDetails }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [slotsList, setSlotsList] = useState([]);

  useEffect(() => {
    getTimeSlotsList();
  }, []);

  const getTimeSlotsList = async () => {
    try {
      setLoading(true);
      let data = await getTimeSlotsService();
      let slotList = data?.map((item, index) => {
        return {
          label: item?.slot_from + "-" + item?.slot_to,
          value: item?.id,
          item,
        };
      });
      setSlotsList(slotList);
      throw data;
    } catch (e) {
      console.log(e, "error");
      setLoading(false);
    }
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true);
      let payload = { property_id: id };
      if (values?.schedule_date && values?.timeslot?.value) {
        payload.schedule_date = values?.schedule_date;
        payload.timeslot = values.timeslot?.value;
      }
      if (values?.schedule_date_2 && values?.timeslot_2?.value) {
        payload.schedule_date_2 = values?.schedule_date_2;
        payload.timeslot_2 = values.timeslot_2?.value;
      }
      if (values?.schedule_date_3 && values?.timeslot_3?.value) {
        payload.schedule_date_3 = values?.schedule_date_3;
        payload.timeslot_3 = values.timeslot_3?.value;
      }
      let data = await schedulePropertyVisit(payload);
      if (data) {
        setScheduleAppointment(false);
        getPropertyDetails();
        setLoading(false);
        resetForm();
      }
      throw data;
    } catch (e) {
      console.log(e, "error");
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      schedule_date: "",
      schedule_date_2: "",
      schedule_date_3: "",
      timeslot: null,
      timeslot_2: null,
      timeslot_3: null,
    },
    validationSchema: Yup.object({
      schedule_date: Yup.string().test("schedule_date", "Please Select Preferred date", function (value) {
        return !!value;
      }),
      timeslot: Yup.object().test(
        "timeslot",
        "Please select a valid time slot.",
        function (value) {
          return !!value;
        }
      ),
    }),
    onSubmit: handleFormSubmit,
  });

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        {/* Preference Date 1 — Mandatory */}
        <Box sx={sectionCard}>
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              color: "#1B2559",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                backgroundColor: "#1A8CFF",
                color: "#fff",
                borderRadius: "50%",
                fontSize: "11px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              1
            </Box>
            {t("Agent_dashboard.Preference Date")} 1 &nbsp;
            <Typography
              component="span"
              sx={{ fontSize: "11px", color: "#1A8CFF", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              ({t("Agent_dashboard.Mandatory")})
            </Typography>
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <label htmlFor="date-1" style={labelStyle}>
                {t("Agent_dashboard.Preference Date")}
              </label>
              <input
                type="date"
                id="date-1"
                style={dateInputStyle}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => formik.handleChange(e)}
                onBlur={formik.handleBlur}
                value={formik.values.schedule_date}
                name="schedule_date"
              />
              {formik.touched.schedule_date && formik.errors.schedule_date && (
                <Typography sx={{ color: "#EF4444", fontSize: "11px", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
                  {formik.errors.schedule_date}
                </Typography>
              )}
            </Box>
            <Box>
              <label htmlFor="slot-1" style={labelStyle}>
                {t("Agent_dashboard.Select Slot")}
              </label>
              <CustomSelect
                options={slotsList}
                width={"100%"}
                dropDownZIndex={100000}
                placeholder={t("Agent_dashboard.Select Slot")}
                labelShrink={true}
                controlStyle={{
                  padding: 5,
                  height: 44,
                  borderRadius: "10px",
                  border: "1.5px solid #E8ECF0",
                  backgroundColor: "#F5F7FA",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "13px",
                }}
                name="timeslot"
                value={formik.values.timeslot}
                handleChange={(e) => {
                  formik.setValues((values) => ({ ...values, timeslot: e }));
                }}
                defaultOptions
                isClearable={true}
                error={formik.touched.timeslot && Boolean(formik.errors.timeslot)}
                helperText={formik.touched.timeslot && formik.errors.timeslot}
              />
              {formik.touched.timeslot && formik.errors.timeslot && (
                <Typography sx={{ color: "#EF4444", fontSize: "11px", mt: 0.5, fontFamily: "Poppins, sans-serif" }}>
                  {formik.errors.timeslot}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Preference Date 2 — Optional */}
        <Box sx={sectionCard}>
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              color: "#1B2559",
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                backgroundColor: "#E8ECF0",
                color: "#6B7280",
                borderRadius: "50%",
                fontSize: "11px",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              2
            </Box>
            {t("Agent_dashboard.Preference Date")} 2 &nbsp;
            <Typography
              component="span"
              sx={{ fontSize: "11px", color: "#9CA3AF", fontFamily: "Poppins, sans-serif", fontWeight: 500 }}
            >
              ({t("Agent_dashboard.optional")})
            </Typography>
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box>
              <label htmlFor="date-2" style={labelStyle}>
                {t("Agent_dashboard.Preference Date")}
              </label>
              <input
                type="date"
                id="date-2"
                style={dateInputStyle}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => formik.handleChange(e)}
                value={formik.values.schedule_date_2}
                name="schedule_date_2"
              />
            </Box>
            <Box>
              <label htmlFor="slot-2" style={labelStyle}>
                {t("Agent_dashboard.Select Slot")}
              </label>
              <CustomSelect
                options={slotsList}
                width={"100%"}
                dropDownZIndex={100000}
                labelShrink={true}
                placeholder={t("Agent_dashboard.Select Slot")}
                controlStyle={{
                  padding: 5,
                  height: 44,
                  borderRadius: "10px",
                  border: "1.5px solid #E8ECF0",
                  backgroundColor: "#F5F7FA",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "13px",
                }}
                name="timeslot_2"
                value={formik.values.timeslot_2}
                handleChange={(e) => {
                  formik.setValues((values) => ({ ...values, timeslot_2: e || "" }));
                }}
                defaultOptions
                isClearable={true}
              />
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 1 }}>
          <Button
            onClick={formik.resetForm}
            sx={{
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 500,
              color: "#6B7280",
              border: "1.5px solid #E8ECF0",
              borderRadius: "8px",
              padding: "10px 24px",
              fontSize: "13px",
              "&:hover": {
                backgroundColor: "#F5F7FA",
                borderColor: "#C0C8D0",
              },
            }}
          >
            {t("Agent_dashboard.Reset")}
          </Button>
          <Button
            type="submit"
            disabled={loading}
            sx={{
              textTransform: "none",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              color: "#fff",
              backgroundColor: loading ? "#B0C4DE" : "#1A8CFF",
              borderRadius: "8px",
              padding: "10px 32px",
              fontSize: "13px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: loading ? "#B0C4DE" : "#1478D9",
                boxShadow: "0 4px 12px rgba(26,140,255,0.3)",
              },
            }}
          >
            {loading ? "Submitting..." : t("Agent_dashboard.Apply")}
          </Button>
        </Box>
      </form>
    </>
  );
};

export default ScheduleVisit;
